import { createWebHistory, createRouter } from "vue-router";
import routes from "./routes";
import appConfig from "../../app.config";
import api from "@/services/api";
import store from "@/store/store";

const router = createRouter({
  history: createWebHistory(),
  routes,
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  if (!authRequired) return next();

  const storedPermissions = localStorage.getItem("permissions");
  const userPermissions = storedPermissions ? JSON.parse(storedPermissions) : [];

  if (routeTo.meta.permissions) {
    const hasPermission = routeTo.matched.some((route) => {
      return route.meta.permissions?.some((perm) => userPermissions.includes(perm));
    });

    if (!hasPermission) {
      localStorage.removeItem("profile");
      localStorage.removeItem("token");
      localStorage.removeItem("permissions");
      
      next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
    }
  }

  await api
    .getProfile()
    .then((response) => {
      store.dispatch("auth/storeProfileData", response.data.data);
      next();
    })
    .catch(() => {
      localStorage.removeItem("profile");
      localStorage.removeItem("token");
      localStorage.removeItem("permissions");

      next({ name: "login", query: { redirectFrom: routeTo.fullPath } });
    });
});

router.beforeEach((routeTo, routeFrom, next) => {
  const publicPages = ["/login", "/register", "/forgot-password"];
  const authpage = !publicPages.includes(routeTo.path);
  const loggeduser = localStorage.getItem("profile");

  if (authpage && !loggeduser) {
    return next("/login");
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
